import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  ShieldCheck,
  UserPlus,
  Key,
  Edit2,
  Trash2,
  Search,
  Crown,
  Shield,
  CheckCircle,
  XCircle,
  RefreshCw,
  Eye,
  EyeOff,
  Sparkles,
  UserCheck,
} from 'lucide-react';
import { rbacApi, AdminUser } from '@/services/rbacApi';
import { useAuthStore } from '@/stores/authStore';

export const AdminUsersList: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'admin' as 'super_admin' | 'admin',
  });

  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    role: 'admin',
    status: 'active',
  });

  const [newPassword, setNewPassword] = useState<string>('');
  const [showPasswordText, setShowPasswordText] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await rbacApi.getAdminUsers();
      setUsers(data);
    } catch (error: any) {
      toast.error(error.message || 'Admin kullanıcıları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filtered Users
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const matchesSearch =
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRole =
        roleFilter === 'all' ||
        (roleFilter === 'super_admin' && (u.role === 'super_admin' || u.isSuperAdmin)) ||
        (roleFilter === 'admin' && u.role === 'admin' && !u.isSuperAdmin);

      return matchesSearch && matchesRole;
    });
  }, [users, searchTerm, roleFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = users.length;
    const superAdmins = users.filter((u) => u.role === 'super_admin' || u.isSuperAdmin).length;
    const subAdmins = total - superAdmins;
    const active = users.filter((u) => u.status === 'active').length;
    return { total, superAdmins, subAdmins, active };
  }, [users]);

  // Helper: Generate Random Password
  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let pass = '';
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass;
  };

  // Handle Create Admin
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Lütfen gerekli alanları doldurun');
      return;
    }
    if (createForm.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      setSubmitting(true);
      await rbacApi.createAdminUser(createForm);
      toast.success('Yeni Admin kullanıcısı başarıyla oluşturuldu!');
      setShowCreateModal(false);
      setCreateForm({ name: '', email: '', phone: '', password: '', role: 'admin' });
      fetchUsers();
    } catch (err: any) {
      toast.error(err.message || 'Admin oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  // Open Edit Modal
  const openEditModal = (u: AdminUser) => {
    setSelectedUser(u);
    setEditForm({
      name: u.name || '',
      phone: u.phone || '',
      role: u.role || 'admin',
      status: u.status || 'active',
    });
    setShowEditModal(true);
  };

  // Handle Edit Submit
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setSubmitting(true);
      await rbacApi.updateAdminUser(selectedUser.uid, editForm);
      toast.success('Admin hesabı başarıyla güncellendi!');
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.message || 'Güncelleme başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  // Open Password Modal
  const openPasswordModal = (u: AdminUser) => {
    setSelectedUser(u);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  // Handle Change Password Submit
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    if (!newPassword || newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      setSubmitting(true);
      await rbacApi.changeAdminPassword(selectedUser.uid, newPassword);
      toast.success(`${selectedUser.name} için yeni şifre başarıyla kaydedildi!`);
      setShowPasswordModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (err: any) {
      toast.error(err.message || 'Şifre değiştirilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Delete / Deactivate
  const handleDeleteUser = async (u: AdminUser) => {
    if (u.uid === currentUser?.uid) {
      toast.error('Kendi hesabınızı silemezsiniz');
      return;
    }

    if (!window.confirm(`${u.name} kullanıcısını pasife alıp silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      await rbacApi.deleteAdminUser(u.uid);
      toast.success('Admin hesabı pasife alındı');
      fetchUsers();
    } catch (err: any) {
      toast.error(err.message || 'Silme işlemi başarısız');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 text-gray-100">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-white">
            <UserCheck className="w-7 h-7 text-indigo-400" />
            Admin Hesap Yönetimi
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Süper Admin ve Alt Admin hesaplarını listeleyin, yeni yetkili ekleyin veya şifrelerini değiştirin.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchUsers}
            className="p-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg border border-gray-700 transition flex items-center gap-2"
            title="Yenile"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg shadow-lg shadow-indigo-600/30 transition flex items-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Yeni Admin Ekle
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-800/80 border border-gray-700/60 p-4 rounded-xl flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-gray-400 uppercase font-semibold">Toplam Admin</p>
            <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
          </div>
          <div className="p-3 bg-indigo-500/10 text-indigo-400 rounded-lg border border-indigo-500/20">
            <ShieldCheck className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-gray-800/80 border border-gray-700/60 p-4 rounded-xl flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-gray-400 uppercase font-semibold">Süper Adminler</p>
            <p className="text-2xl font-bold text-amber-400 mt-1">{stats.superAdmins}</p>
          </div>
          <div className="p-3 bg-amber-500/10 text-amber-400 rounded-lg border border-amber-500/20">
            <Crown className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-gray-800/80 border border-gray-700/60 p-4 rounded-xl flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-gray-400 uppercase font-semibold">Alt / Operasyon Admin</p>
            <p className="text-2xl font-bold text-blue-400 mt-1">{stats.subAdmins}</p>
          </div>
          <div className="p-3 bg-blue-500/10 text-blue-400 rounded-lg border border-blue-500/20">
            <Shield className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-gray-800/80 border border-gray-700/60 p-4 rounded-xl flex items-center justify-between shadow-sm">
          <div>
            <p className="text-xs text-gray-400 uppercase font-semibold">Aktif Hesaplar</p>
            <p className="text-2xl font-bold text-emerald-400 mt-1">{stats.active}</p>
          </div>
          <div className="p-3 bg-emerald-500/10 text-emerald-400 rounded-lg border border-emerald-500/20">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-gray-800/60 border border-gray-700/60 p-4 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="İsim veya e-posta ile ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-indigo-500"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs text-gray-400 font-medium">Rol Filtresi:</span>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-indigo-500"
          >
            <option value="all">Tüm Rolleri Göster</option>
            <option value="super_admin">👑 Süper Adminler</option>
            <option value="admin">🛡️ Alt Adminler</option>
          </select>
        </div>
      </div>

      {/* Admin Users Table */}
      <div className="bg-gray-800/80 border border-gray-700/60 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-300">
            <thead className="bg-gray-900/80 text-xs uppercase text-gray-400 border-b border-gray-700/60">
              <tr>
                <th className="px-6 py-3.5">Kullanıcı</th>
                <th className="px-6 py-3.5">Rol Hiyerarşisi</th>
                <th className="px-6 py-3.5">Telefon</th>
                <th className="px-6 py-3.5">Durum</th>
                <th className="px-6 py-3.5">Kayıt Tarihi</th>
                <th className="px-6 py-3.5 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/40">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-indigo-400 mb-2" />
                    Yöneticiler yükleniyor...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400">
                    Arama kriterlerine uygun admin kullanıcısı bulunamadı.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => {
                  const isSuper = u.role === 'super_admin' || u.isSuperAdmin;
                  const isActive = u.status === 'active';

                  return (
                    <tr key={u.uid} className="hover:bg-gray-700/30 transition">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-full font-bold flex items-center justify-center text-sm border shadow-inner ${
                              isSuper
                                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                                : 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40'
                            }`}
                          >
                            {u.name ? u.name.charAt(0).toUpperCase() : 'A'}
                          </div>
                          <div>
                            <div className="font-semibold text-white flex items-center gap-1.5">
                              {u.name}
                              {u.uid === currentUser?.uid && (
                                <span className="text-[10px] bg-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded border border-indigo-500/30">
                                  Siz
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400">{u.email}</div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {isSuper ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                            <Crown className="w-3.5 h-3.5 text-amber-400" />
                            Süper Admin
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30">
                            <Shield className="w-3.5 h-3.5 text-blue-400" />
                            Alt Admin (Operasyon)
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-xs text-gray-400">{u.phone || '-'}</td>

                      <td className="px-6 py-4">
                        {isActive ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                            <CheckCircle className="w-3 h-3" />
                            Aktif
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-500/15 text-gray-400 border border-gray-500/30">
                            <XCircle className="w-3 h-3" />
                            Pasif
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-xs text-gray-400">
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString('tr-TR') : '-'}
                      </td>

                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openPasswordModal(u)}
                            className="p-2 bg-gray-700/60 hover:bg-amber-600/30 text-gray-300 hover:text-amber-300 rounded-lg border border-gray-600/50 transition"
                            title="Şifre Değiştir"
                          >
                            <Key className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => openEditModal(u)}
                            className="p-2 bg-gray-700/60 hover:bg-indigo-600/30 text-gray-300 hover:text-indigo-300 rounded-lg border border-gray-600/50 transition"
                            title="Düzenle"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>

                          {u.uid !== currentUser?.uid && (
                            <button
                              onClick={() => handleDeleteUser(u)}
                              className="p-2 bg-gray-700/60 hover:bg-rose-600/30 text-gray-300 hover:text-rose-300 rounded-lg border border-gray-600/50 transition"
                              title="Sil / Pasife Al"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= MODAL: YENİ ADMİN EKLE ================= */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-indigo-400" />
                Yeni Admin Kullanıcısı Ekle
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white text-xl font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase mb-1">
                  Ad Soyad <span className="text-rose-400">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="Örn: Ahmet Yılmaz"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase mb-1">
                  E-Posta Adresi <span className="text-rose-400">*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="admin@tazebijest.com"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase mb-1">Telefon</label>
                <input
                  type="text"
                  placeholder="05551112233"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-gray-300 uppercase">
                    Giriş Şifresi <span className="text-rose-400">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setCreateForm({ ...createForm, password: generateRandomPassword() })
                    }
                    className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 font-medium"
                  >
                    <Sparkles className="w-3 h-3" /> Rastgele Şifre Üret
                  </button>
                </div>
                <input
                  type="text"
                  required
                  minLength={6}
                  placeholder="En az 6 karakter"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3.5 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase mb-1">
                  Admin Yetki Seviyesi (Rol)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCreateForm({ ...createForm, role: 'admin' })}
                    className={`p-3 rounded-xl border text-left flex flex-col gap-1 transition ${
                      createForm.role === 'admin'
                        ? 'bg-blue-600/20 border-blue-500 text-blue-200'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <span className="font-semibold text-sm flex items-center gap-1">
                      <Shield className="w-4 h-4 text-blue-400" /> Alt Admin
                    </span>
                    <span className="text-[11px] text-gray-400">Operasyonel yetkiler (Ürün/Sipariş)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setCreateForm({ ...createForm, role: 'super_admin' })}
                    className={`p-3 rounded-xl border text-left flex flex-col gap-1 transition ${
                      createForm.role === 'super_admin'
                        ? 'bg-amber-600/20 border-amber-500 text-amber-200'
                        : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <span className="font-semibold text-sm flex items-center gap-1">
                      <Crown className="w-4 h-4 text-amber-400" /> Süper Admin
                    </span>
                    <span className="text-[11px] text-gray-400">Tam yetki + Admin yönetimi</span>
                  </button>
                </div>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-800">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg shadow-lg shadow-indigo-600/30 transition disabled:opacity-50"
                >
                  {submitting ? 'Kaydediliyor...' : 'Admin Hesabını Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= MODAL: ADMİN DÜZENLE ================= */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-indigo-400" />
                Admin Hesabını Düzenle
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-white font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">E-Posta (Değiştirilemez)</label>
                <input
                  type="text"
                  disabled
                  value={selectedUser.email}
                  className="w-full bg-gray-800/50 border border-gray-800 rounded-lg px-3.5 py-2.5 text-sm text-gray-400 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase mb-1">Ad Soyad</label>
                <input
                  type="text"
                  required
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase mb-1">Telefon</label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase mb-1">Rol</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="admin">🛡️ Alt Admin (Operasyon)</option>
                  <option value="super_admin">👑 Süper Admin (Tam Yetkili)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 uppercase mb-1">Durum</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="active">✅ Aktif</option>
                  <option value="passive">⛔ Pasif</option>
                </select>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-800">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg shadow-lg shadow-indigo-600/30 transition disabled:opacity-50"
                >
                  {submitting ? 'Güncelleniyor...' : 'Değişiklikleri Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= MODAL: ŞİFRE DEĞİŞTİR ================= */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-400" />
                Şifre Değiştir
              </h2>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-400 hover:text-white font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit} className="p-6 space-y-4">
              <div className="bg-gray-800/60 p-3 rounded-lg border border-gray-700/60 text-xs">
                <p className="text-gray-400">Hedef Admin:</p>
                <p className="font-semibold text-white text-sm">{selectedUser.name}</p>
                <p className="text-gray-400 text-xs">{selectedUser.email}</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-gray-300 uppercase">
                    Yeni Şifre <span className="text-rose-400">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setNewPassword(generateRandomPassword())}
                    className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 font-medium"
                  >
                    <Sparkles className="w-3 h-3" /> Rastgele Şifre Üret
                  </button>
                </div>

                <div className="relative">
                  <input
                    type={showPasswordText ? 'text' : 'password'}
                    required
                    minLength={6}
                    placeholder="Yeni şifreyi girin..."
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-3.5 pr-10 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-amber-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordText(!showPasswordText)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-white"
                  >
                    {showPasswordText ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-800">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 text-sm text-gray-400 hover:text-white"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg shadow-lg shadow-amber-600/30 transition disabled:opacity-50"
                >
                  {submitting ? 'Şifre Güncelleniyor...' : 'Şifreyi Güncelle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersList;
